import './OfferHeaderMenuDialog.scss';
import { FC, MouseEvent, ReactNode, useRef, useState } from 'react';
import Menu from '@srm/ui/menu';
import { IListElement } from '@srm/ui/types/IListElement';

export interface IOfferHeaderMenuDialogProps {
    children: ReactNode;
    list: IListElement[];
}

/**
 * Шапка колонки предложения с меню действий (`Menu` пакета). Кликабельна вся шапка: якорь меню — кнопка во всю шапку
 * под её содержимым (имя «Действия с предложением», фокус, `aria-haspopup`/`aria-expanded`, Enter, пробел и стрелки), а
 * щелчок по содержимому открывает то же меню. Свои кнопки шапки (полоса решения) гасят всплытие и меню не открывают.
 * Без действий — шапка без меню.
 */
const OfferHeaderMenuDialog: FC<IOfferHeaderMenuDialogProps> = ({ children, list }) => {
    const [opened, setOpened] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    // Меню было открыто в момент нажатия: его уже закрыл сам щелчок мимо кнопки (поповер — по mousedown), щелчок по
    // шапке не должен открыть его снова.
    const openedAtPress = useRef(false);
    const interactive = list.length > 0;

    const onClick = (event: MouseEvent<HTMLDivElement>) => {
        const wasOpened = openedAtPress.current;
        openedAtPress.current = false;
        const target = event.target as Node;
        // пункт меню (портал) всплывает по дереву React, а не DOM; кнопку-якорь меню переключает само
        if (
            !interactive ||
            wasOpened ||
            !event.currentTarget.contains(target) ||
            triggerRef.current?.contains(target)
        ) {
            return;
        }
        setOpened(true);
    };

    return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- щелчок мыши по шапке; с клавиатуры меню открывает кнопка-якорь
        <div
            className="offer-cell-header-menu"
            onPointerDown={() => {
                openedAtPress.current = opened;
            }}
            onClick={onClick}
        >
            {interactive && (
                <Menu
                    className="offer-cell-header-menu__anchor"
                    list={list}
                    opened={opened}
                    onOpenChange={setOpened}
                    position="bottom-end"
                    // постоянная ширина: длинные названия действий переносятся, а не растягивают панель
                    width={360}
                >
                    <button
                        ref={triggerRef}
                        type="button"
                        className="offer-cell-header-menu__trigger"
                        aria-label="Действия с предложением"
                    />
                </Menu>
            )}
            {children}
        </div>
    );
};

export default OfferHeaderMenuDialog;
